/**
 * Bakes free-standing added text (dates, stamps, notes placed anywhere on a
 * page — see AddTextModal.tsx) into the PDF. Unlike cover-and-replace text
 * edits (utils/textEdit.ts) there's no existing run to cover, but it still
 * matches the document's own font via pageFonts.ts (tries the page's
 * embedded fonts first, standard font only as a last resort) — otherwise
 * added text would always come out in a generic Helvetica no matter what
 * typeface the rest of the document actually uses.
 */
import { PDFDocument, PDFFont, rgb } from 'pdf-lib';
// @ts-ignore — no published types; the runtime default export is the fontkit instance pdf-lib expects.
import fontkit from '@pdf-lib/fontkit';
import { ProcessedPage } from './pdfProcessor';
import { PageFontCandidate, resolveFontForText, embedStandardFallback } from './pageFonts';

async function bakeOnce(arrayBuffer: ArrayBuffer, pages: ProcessedPage[], useExtractedFonts: boolean): Promise<ArrayBuffer> {
  const doc = await PDFDocument.load(arrayBuffer.slice(0), { ignoreEncryption: true });
  doc.registerFontkit(fontkit);
  const candidateCache = new Map<number, PageFontCandidate[]>();
  const fallbackCache = new Map<string, PDFFont>();

  for (const p of pages) {
    if (!p.addedTexts?.length) continue;
    const page = doc.getPage(p.pageIndex);
    for (const added of p.addedTexts) {
      const { font } = useExtractedFonts
        ? await resolveFontForText(doc, page, p.pageIndex, added.text, undefined, candidateCache, fallbackCache)
        : { font: await embedStandardFallback(doc, undefined, fallbackCache) };
      const [r, g, b] = added.color;
      page.drawText(added.text, {
        x: added.x,
        y: added.y,
        size: added.fontSize,
        font,
        color: rgb(r, g, b),
      });
    }
  }

  const bytes = await doc.save();
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

export async function bakeAddedTexts(arrayBuffer: ArrayBuffer, pages: ProcessedPage[]): Promise<ArrayBuffer> {
  const hasText = pages.some(p => p.addedTexts && p.addedTexts.length > 0);
  if (!hasText) return arrayBuffer;

  try {
    return await bakeOnce(arrayBuffer, pages, true);
  } catch (err) {
    // Same rare real-world-font embedding failures as textEdit.ts can hit
    // here too — retry with standard fonts only so added text always makes
    // it into the export.
    console.error('bakeAddedTexts: custom font embedding failed, retrying with standard fonts only:', err);
    return bakeOnce(arrayBuffer, pages, false);
  }
}

export function makeAddedTextId(): string {
  return `at_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}
